import { createSelector } from 'reselect'
import { initializedSelector } from '@chegg-wt/core/state/app/selectors'
import { isProSelector } from '@chegg-wt/core/state/user/selectors'
import { Config } from '@chegg-wt/core/config'
import { pipe } from 'rxjs'
import { map } from '@chegg-wt/core/utils/functions'
import { sortCitationsByKey } from '@chegg-wt/core/utils/projects'
import { Citation } from '@chegg-wt/core/types/redux-citation-api'
import {
  mostRecentCitationIdSelector,
  incompleteCitationIdSelector,
} from '../citation/selectors'
import { AppState } from '../types'
import { isMovedCitationsSuccessSelector } from '../movedCitations/selectors'

// should use `sortedCitationsSelector` when wanting to pull `citations` state
export const citationsSelector = (state: AppState) => state.bibliography.citations

export const sortKeySelector = (state: AppState) => state.bibliography.sortKey

export const totalCitationsSelector = (state: AppState) =>
  state.bibliography.citations.length
export const isCitationsFetchingSelector = (state: AppState) =>
  state.bibliography.isFetching
export const currentCitationsPageSelector = (state: AppState) => state.bibliography.page

export const sortedCitationsSelector = createSelector(
  [citationsSelector, sortKeySelector],
  sortCitationsByKey
)

const identifyNew = (recentCitationId: string) => (citation: Citation) =>
  citation.id === recentCitationId ? { ...citation, isNew: true } : citation
const identifyIncomplete = (incompleteCitationId: string) => (citation: Citation) =>
  citation.id === incompleteCitationId ? { ...citation, isIncomplete: true } : citation

export const completedCitationsSelector = createSelector(
  [sortedCitationsSelector, mostRecentCitationIdSelector, incompleteCitationIdSelector],
  (citations, recentCitationId, incompleteCitationId): Citation[] =>
    pipe(
      map(identifyNew(recentCitationId)),
      map(identifyIncomplete(incompleteCitationId))
    )(citations)
)

export const isWarningSelector = createSelector(
  [completedCitationsSelector],
  (citations) => citations.map((citation) => citation.isIncomplete).includes(true)
)

export const isNewSelector = createSelector([completedCitationsSelector], (citations) =>
  citations.map((citation) => citation.isNew).includes(true)
)

export const selectedCitationsSelector = createSelector(
  [sortedCitationsSelector],
  (citations) => citations.filter((citation) => citation.selected)
)

export const isSuccessSelector = createSelector(
  [isNewSelector, isMovedCitationsSuccessSelector],
  (isNew, isMoved) => isNew || isMoved
)

export const isDataReadySelector = createSelector(
  [initializedSelector, isCitationsFetchingSelector],
  (isInitialized, isFetching) => isInitialized && !isFetching
)

export const shouldWarnOnCitationCountSelector = createSelector(
  [isDataReadySelector, isProSelector, totalCitationsSelector],
  (isDataReady, isPro, totalCitationCount) =>
    isDataReady &&
    !isPro &&
    !!Config.bibliographyCitationCountWarningThreshold &&
    totalCitationCount >= Config.bibliographyCitationCountWarningThreshold
)

export const shouldBlockOnCitationCountSelector = createSelector(
  [isDataReadySelector, isProSelector, totalCitationsSelector],
  (isDataReady, isPro, totalCitationCount) =>
    isDataReady &&
    !isPro &&
    !!Config.bibliographyCitationCountThreshold &&
    totalCitationCount >= Config.bibliographyCitationCountThreshold
)
